import React, { useState, useContext, useMemo } from 'react';
import { CheckCircleIcon, ExclamationIcon } from '@heroicons/react/solid';
import * as AlertDialog from '@radix-ui/react-alert-dialog';
import * as Progress from '@radix-ui/react-progress';
import { functions, db } from '../../../../firebase';
import { httpsCallable } from 'firebase/functions';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { PatientDataContext } from '../../../../context/PatientDataContext';

/**
 * PreAssessmentReportWizard Component
 * -----------------------------------
 * A dialog wizard that guides clinicians through generating the Pre-Screening Report.
 * 
 * This wizard:
 * 1. Displays which patient history forms will be summarised
 * 2. Collects the clinician's recommended next steps for the Formulation section
 * 3. Triggers the generatePreAssessmentReport cloud function
 * 4. Shows progress feedback during generation
 * 
 * The recommended next steps text is required and will be summarised by the LLM
 * into a professional paragraph for the "Formulation" section of the report.
 * 
 * Props:
 * - open: Boolean controlling dialog visibility
 * - onOpenChange: Callback when dialog open state changes
 * - patientId: ID of the patient
 * - clinicianId: ID of the current clinician
 * - allHistoryFormsComplete: Boolean indicating if all forms are complete
 */
export default function PreAssessmentReportWizard({ 
  open, 
  onOpenChange, 
  patientId, 
  clinicianId,
  allHistoryFormsComplete 
}) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showProgressDialog, setShowProgressDialog] = useState(false);
  const [progressValue, setProgressValue] = useState(0);
  const [error, setError] = useState(null);
  const { patientDataModel, updatePatientFirestore } = useContext(PatientDataContext);
  
  // State for the clinician's recommended next steps input
  // This text will be summarised by the LLM for the Formulation section
  const [recommendedNextSteps, setRecommendedNextSteps] = useState('');

  /**
   * Get list of completed patient history forms to display.
   * Shows form name and completion status.
   */
  const completedForms = useMemo(() => {
    const forms = [];
    const formData = patientDataModel?.formData || {};
    const dataModel = patientDataModel?.dataModel || {};
    const preAppointmentInfo = dataModel['1_preAppointmentInformation'] || {};
    const sections = preAppointmentInfo.sections || {};
    
    // Find the Patient History section
    let patientHistorySection = null;
    for (const section of Object.values(sections)) {
      if (section.internalName === "Patient History") {
        patientHistorySection = section;
        break;
      }
    }
    
    if (!patientHistorySection || !patientHistorySection.domains) {
      return forms;
    }
    
    const domains = patientHistorySection.domains || {};
    for (const [domainKey, domain] of Object.entries(domains)) {
      const displayName = domain.displayName;
      
      // Find matching form in formData
      let matchingForm = null;
      for (const form of Object.values(formData)) {
        if (form?.formTitle === displayName) {
          matchingForm = form;
          break;
        }
      }
      
      const progressPercentage = matchingForm?.progress?.progressPercentage || 0;
      
      forms.push({
        id: domainKey,
        name: displayName,
        progress: progressPercentage,
        isComplete: progressPercentage >= 100
      });
    }
    
    return forms;
  }, [patientDataModel]);

  /**
   * Report sections that will be generated.
   * Excludes MSE, Risk Assessment, and Diagnostic Formulation.
   */
  const sectionsToGenerate = [
    "Presenting Complaint",
    "Developmental History",
    "Personal and Social History",
    "Lifestyle History",
    "Forensic History",
    "Family History",
    "Medical History",
    "Psychiatric History"
  ];

  /**
   * Handle the generation process.
   * 1. Sets processing status in Firestore
   * 2. Triggers the cloud function
   * 3. Shows progress feedback
   * 4. Closes the wizard on completion
   */
  const handleGenerateReport = async () => {
    try {
      setIsSubmitting(true);
      setError(null);
      
      // Show progress dialog
      setProgressValue(0);
      setShowProgressDialog(true);
      
      // Set processing status + timestamp in Firestore.  The timestamp
      // enables DocumentsDashboard self-healing to detect and reset stuck
      // statuses after a configurable timeout.
      console.log("Setting Pre-Assessment Report processing status to 'processing'");
      const patientDocRef = doc(db, 'patients', patientId);
      await updateDoc(patientDocRef, {
        'dataModel.6_manageReports.preARReportProcessingStatus': 'processing',
        'dataModel.6_manageReports.preARReportProcessingStartedAt': serverTimestamp(),
      });
      
      // Simulate initial progress for UX feedback
      await simulateInitialProgress();
      
      // Close wizard immediately - user doesn't need to wait
      setTimeout(() => {
        setShowProgressDialog(false);
        onOpenChange(false);
      }, 1500);
      
      // Fire the cloud function (fire-and-forget pattern)
      // The Firestore listener in PreAssessmentDocuments will pick up status changes
      const generatePreAssessmentReport = httpsCallable(functions, 'generatePreAssessmentReport');
      
      generatePreAssessmentReport({
        patientId,
        clinicianId,
        // Pass the clinician's recommended next steps for the Formulation section
        // The backend will summarise this text using the LLM
        recommendedNextSteps: recommendedNextSteps.trim()
      })
        .then((result) => {
          console.log('Pre-Assessment Report generation completed:', result.data);
        })
        .catch((error) => {
          console.error('Pre-Assessment Report generation failed:', error);

          // deadline-exceeded means the client timed out but the CF is still
          // running server-side — don't overwrite with 'error'.
          if (error.code === 'deadline-exceeded') {
            console.log('Client-side timeout occurred, but function continues processing in background');
            return;
          }

          updatePatientFirestore(patientId, {
            '6_manageReports': {
              preARReportProcessingStatus: 'error'
            }
          }).catch(err => {
            console.error('Failed to update error status:', err);
          });
        });
      
    } catch (error) {
      console.error('Error initiating Pre-Assessment Report generation:', error);
      setError(error.message || 'An unexpected error occurred');
      
      // Update Firestore with error status
      try {
        await updatePatientFirestore(patientId, {
          '6_manageReports': {
            preARReportProcessingStatus: 'error'
          }
        });
      } catch (err) {
        console.error('Failed to update error status:', err);
      }
      
      // Keep error dialog open for user to see
      setProgressValue(100);
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Simulates initial progress for better UX feedback.
   * Shows the user something is happening before we close the wizard.
   */
  const simulateInitialProgress = async () => {
    return new Promise(resolve => {
      setTimeout(() => setProgressValue(30), 400);
      setTimeout(() => setProgressValue(60), 800);
      setTimeout(() => {
        setProgressValue(100);
        resolve();
      }, 1200);
    });
  };

  // Count stats for display
  const completedCount = completedForms.filter(f => f.isComplete).length;
  const totalCount = completedForms.length;

  return (
    <>
      {/* Main Wizard Dialog */}
      <AlertDialog.Root open={open} onOpenChange={onOpenChange}>
        <AlertDialog.Portal>
          <AlertDialog.Overlay className="radix-dialog-overlay-motion fixed inset-0 bg-black/40 z-50" />
          <AlertDialog.Content className="radix-dialog-content-centered-motion fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 max-h-[85vh] w-[90vw] max-w-[600px] bg-white rounded-xl shadow-lg p-6 z-50 overflow-hidden flex flex-col">
            <AlertDialog.Title className="text-xl font-semibold text-gray-900 mb-2">
              Generate Pre-Screening Report
            </AlertDialog.Title>
            <AlertDialog.Description className="text-gray-600 mb-4">
              This report will summarise the patient's history form responses before the clinical assessment.
            </AlertDialog.Description>

            <div className="flex-grow overflow-y-auto p-0.5 mb-4" style={{ maxHeight: '60vh' }}>
              {/* Recommended Next Steps Input - Primary focus of the wizard */}
              <div className={`rounded-lg p-4 mb-4 border-2 ${
                recommendedNextSteps.trim() ? 'bg-violet-50 border-violet-200' : 'bg-gray-50 border-gray-200'
              }`}>
                <label 
                  htmlFor="recommendedNextSteps" 
                  className="block text-sm font-medium text-gray-800 mb-2"
                >
                  Please write your recommended next steps for the Formulation section of the Pre-Screening Report:
                  <span className="text-red-500 ml-1">*</span>
                </label>
                <textarea
                  id="recommendedNextSteps"
                  value={recommendedNextSteps}
                  onChange={(e) => setRecommendedNextSteps(e.target.value)}
                  placeholder="Enter your recommended next steps here..."
                  className={`w-full h-32 p-3 border rounded-lg text-sm resize-none focus:outline-none focus:ring-2 ${
                    recommendedNextSteps.trim() 
                      ? 'border-violet-300 focus:ring-violet-500 focus:border-violet-500' 
                      : 'border-gray-300 focus:ring-violet-500 focus:border-violet-500'
                  }`}
                  aria-label="Recommended Next Steps"
                  aria-required="true"
                />
              </div>

              {/* Compact Summary of Forms and Sections */}
              <div className="grid grid-cols-2 gap-3 mb-4">
                {/* Patient History Forms Status */}
                <div className="bg-green-50 rounded-lg p-3">
                  <h4 className="text-xs font-medium text-green-800 mb-2">
                    Patient History Forms ({completedCount}/{totalCount})
                  </h4>
                  <div className="space-y-1 max-h-24 overflow-y-auto">
                    {completedForms.map((form) => (
                      <div key={form.id} className="flex items-center text-xs">
                        {form.isComplete ? (
                          <CheckCircleIcon className="w-3.5 h-3.5 mr-1.5 text-green-600 flex-shrink-0" />
                        ) : (
                          <ExclamationIcon className="w-3.5 h-3.5 mr-1.5 text-amber-500 flex-shrink-0" />
                        )}
                        <span className={form.isComplete ? 'text-green-700' : 'text-amber-700'}>
                          {form.name}
                          {!form.isComplete && ` (${Math.round(form.progress)}%)`}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Sections to Generate */}
                <div className="bg-blue-50 rounded-lg p-3">
                  <h4 className="text-xs font-medium text-blue-800 mb-2">
                    Sections to Generate
                  </h4>
                  <div className="space-y-1 max-h-24 overflow-y-auto">
                    {sectionsToGenerate.map((section, index) => (
                      <div key={index} className="flex items-center text-xs text-blue-700">
                        <CheckCircleIcon className="w-3.5 h-3.5 mr-1.5 text-blue-600 flex-shrink-0" />
                        {section}
                      </div>
                    ))}
                    {/* Formulation is also generated from the clinician input */}
                    <div className="flex items-center text-xs text-violet-700 font-medium">
                      <CheckCircleIcon className="w-3.5 h-3.5 mr-1.5 text-violet-600 flex-shrink-0" />
                      Formulation (from your input)
                    </div>
                  </div>
                </div>
              </div>

              {/* Warning if forms not complete */}
              {!allHistoryFormsComplete && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-start">
                    <ExclamationIcon className="w-4 h-4 text-amber-600 mr-2 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-amber-800">
                      All patient history forms must be 100% complete before generating the report.
                    </p>
                  </div>
                </div>
              )}
            </div>

            <div className="font-semibold flex justify-end gap-3 mt-auto pt-2 border-t">
              <AlertDialog.Cancel className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors">
                Cancel
              </AlertDialog.Cancel>
              <AlertDialog.Action
                onClick={handleGenerateReport}
                disabled={isSubmitting || !allHistoryFormsComplete || !recommendedNextSteps.trim()}
                className={`px-4 py-2 bg-violet-600 text-white rounded-lg transition-colors ${
                  isSubmitting || !allHistoryFormsComplete || !recommendedNextSteps.trim()
                    ? 'opacity-50 cursor-not-allowed'
                    : 'hover:bg-violet-700'
                }`}
              >
                {isSubmitting ? 'Generating...' : 'Generate Pre-Screening Report'}
              </AlertDialog.Action>
            </div>
          </AlertDialog.Content>
        </AlertDialog.Portal>
      </AlertDialog.Root>

      {/* Progress Dialog - Shows briefly before closing */}
      <AlertDialog.Root open={showProgressDialog} onOpenChange={setShowProgressDialog}>
        <AlertDialog.Portal>
          <AlertDialog.Overlay className="radix-dialog-overlay-motion fixed inset-0 bg-black/60 z-50" />
          <AlertDialog.Content className="radix-dialog-content-centered-motion fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg shadow-lg p-6 w-[400px] z-50">
            <AlertDialog.Title className="text-lg font-semibold mb-4">
              Generating Pre-Screening Report
            </AlertDialog.Title>

            <AlertDialog.Description className="text-sm text-gray-600 mb-4">
              Please wait while the report is being prepared. This may take a few minutes.
            </AlertDialog.Description>

            <div className="space-y-6">
              {/* Progress Bar */}
              <div className="w-full">
                <div className="flex justify-between mb-2">
                  <span className="text-sm">Processing patient history data...</span>
                </div>
                <Progress.Root
                  className="h-2 w-full bg-gray-200 rounded-full overflow-hidden"
                  value={progressValue}
                >
                  <Progress.Indicator
                    className={`h-full rounded-full transition-transform duration-[300ms] ease-in-out ${
                      progressValue === 100 ? 'bg-green-600' : 'bg-violet-600'
                    }`}
                    style={{ transform: `translateX(-${100 - progressValue}%)` }}
                  />
                </Progress.Root>
              </div>

              {/* Informational text */}
              <p className="text-xs text-gray-500 italic">
                You can navigate away from this page. The report will continue generating in the background.
              </p>

              {/* Error message if initialization fails */}
              {error && (
                <div className="bg-red-50 border border-red-300 text-red-700 px-4 py-3 rounded relative" role="alert">
                  <strong className="font-bold">Error: </strong>
                  <span className="block sm:inline">{error}</span>
                </div>
              )}
            </div>
          </AlertDialog.Content>
        </AlertDialog.Portal>
      </AlertDialog.Root>
    </>
  );
}
